/**
 * Terms of Use — plain, friendly copy. Linked from footer, signup, and DMs.
 *
 * v2 (Feb 2026): adds explicit anti-piracy prosecution clause and a
 * Canadian jurisdiction / exclusive-venue clause. These map to the two
 * mandatory checkboxes on the signup form + the `TermsAcceptanceModal`
 * shown to existing users when the version bumps.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FileText, ArrowLeft, ShieldAlert, Scale } from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const Section = ({ title, children, testId }) => (
  <section className="mt-6" data-testid={testId}>
    <h2 className="font-heading text-xl font-black text-foreground">{title}</h2>
    <div className="prose prose-sm dark:prose-invert max-w-none mt-2 text-foreground/90">{children}</div>
  </section>
);

const TermsOfUse = () => {
  const { isAuthenticated, API } = useContext(AuthContext);
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !API) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/legal/my-acceptance`);
        setCurrentVersion(data.current_version || '');
        setNeedsAcceptance(!data.up_to_date);
      } catch (_) { /* silent */ }
    })();
  }, [isAuthenticated, API]);

  const acceptFromHere = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/legal/accept-terms`, {
        version: currentVersion,
        accepted_prosecution_clause: true,
        accepted_canadian_jurisdiction: true,
      });
      toast.success('Terms accepted — thanks!');
      setNeedsAcceptance(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record acceptance.');
    } finally {
      setAccepting(false);
    }
  };

  return (
  <div className="min-h-screen bg-background">
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </Link>

      {/* Sticky acceptance CTA — only shows when the current user still needs
          to accept the latest version. Sits at the top so it's obvious what
          to do once they've read the terms. */}
      {needsAcceptance && (
        <div
          className="sticky top-2 z-20 mb-6 rounded-xl border border-primary/50 bg-primary/10 backdrop-blur px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3"
          data-testid="terms-accept-banner"
        >
          <ShieldAlert className="w-5 h-5 text-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-heading font-extrabold text-foreground">
              You still need to accept the updated Terms
            </div>
            <div className="text-xs text-foreground/80">
              Read below, then tap the button to accept the anti-piracy + Canadian jurisdiction clauses.
            </div>
          </div>
          <Button
            onClick={acceptFromHere}
            disabled={accepting}
            className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
            data-testid="accept-terms-inline-btn"
          >
            {accepting ? 'Recording…' : 'I accept both clauses'}
          </Button>
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
          <FileText className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Legal</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground leading-tight" data-testid="terms-of-use-heading">
            Terms of Use
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Last updated February 2026 · Version 2026-02-canada-v1
          </p>
        </div>
      </div>

      <Section title="1. Who we are" testId="tos-section-who">
        <p>
          Freeze Dry Academy (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;the Academy&rdquo;),
          operated by <strong>Peak and Prairie Industries Inc.</strong> in the Province of
          Alberta, Canada, is an educational platform for home &amp; commercial
          freeze-drying. These terms cover the courses, community forum, live
          sessions, direct messaging, storefront, and any tools we build on
          the site.
        </p>
      </Section>

      <Section title="2. Your account" testId="tos-section-account">
        <p>
          You&rsquo;re responsible for anything that happens on your account.
          Keep your password to yourself, don&rsquo;t impersonate anyone else,
          and let us know right away if you think someone else is signed in.
          We may suspend or close accounts that break these terms.
        </p>
      </Section>

      <Section title="3. What you can post" testId="tos-section-post">
        <p>Great content welcome. Not welcome:</p>
        <ul>
          <li>Illegal content, threats, harassment, hate speech, or slurs</li>
          <li>Sexual, graphic, or self-harm content</li>
          <li>Spam, unauthorized ads, phishing, or scams</li>
          <li>Personal info about others without their consent (doxxing)</li>
          <li>Content that infringes copyright, trademark, or other rights</li>
        </ul>
        <p>
          We use automated filters plus human review. Anything flagged can be
          censored, removed, or reviewed by a moderator; repeat offenses can
          get you suspended or banned. See our{' '}
          <Link to="/community-standards" className="underline">Community Standards</Link>{' '}
          for the full guidelines.
        </p>
      </Section>

      <Section title="4. Educational content, not medical advice" testId="tos-section-advisory">
        <p>
          Freeze-drying tips are informational. Nothing here is medical,
          nutritional, or safety advice for your specific situation. Follow
          local food-safety regulations and use common sense.
        </p>
      </Section>

      {/* ============================================================
           5. Anti-piracy clause — the teeth behind our DRM controls.
         ============================================================ */}
      <Section title="5. Course content is licensed to you, not sold" testId="tos-section-piracy">
        <div className="not-prose flex items-start gap-2 p-3 my-3 rounded-xl border border-red-300/60 bg-red-50 dark:bg-red-950/40">
          <ShieldAlert className="w-4 h-4 text-red-700 dark:text-red-300 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-800 dark:text-red-200 leading-relaxed m-0">
            <strong>Read this carefully.</strong> Course content is protected by
            copyright, contract, and technical measures. Violations are pursued.
          </p>
        </div>
        <p>
          All lessons, videos, images, PDFs, transcripts, quizzes, worksheets,
          written materials, AI-generated study aids, and downloadable
          resources on Freeze Dry Academy are the exclusive intellectual
          property of Peak and Prairie Industries Inc. and its licensors,
          protected under the <em>Copyright Act</em> (Canada), the U.S.
          Copyright Act (17 U.S.C.), the Berne Convention, and applicable
          international treaties.
        </p>
        <p>
          Your subscription grants you a <strong>personal, non-transferable,
          revocable licence</strong> to view course materials for private
          educational use only. You may <strong>not</strong>, directly or
          through any third party:
        </p>
        <ul>
          <li>Copy, screenshot for redistribution, download, scrape, screen-record, transcribe, or otherwise reproduce course content;</li>
          <li>Republish, resell, sublicense, share, upload, or make available course content on any other platform, forum, cloud drive, or social network;</li>
          <li>Circumvent, disable, or interfere with the platform&rsquo;s content-protection controls (watermarks, copy blockers, drip-release gating, session watermarks);</li>
          <li>Use course content to train, fine-tune, or ground any machine-learning model, AI assistant, or automated summarisation system;</li>
          <li>Enrol, then request a refund after downloading or transcribing significant portions of a course (see Section 8).</li>
        </ul>
        <p>
          <strong>Enforcement.</strong> We monitor for infringement using
          per-user watermarks, session fingerprints, and third-party
          takedown services. Where infringement is detected, we will pursue
          all available remedies including — but not limited to —
          <strong> statutory damages up to CAD $20,000 per infringed work</strong>{' '}
          under s. 38.1 of the Copyright Act (Canada), <strong>injunctive relief</strong>{' '}
          to compel takedown, <strong>recovery of legal costs and disgorgement of profits</strong>,
          and, where the conduct is willful and commercial, <strong>criminal referral</strong>{' '}
          under s. 42 of the Copyright Act. Repeat infringers will be
          terminated without refund.
        </p>
      </Section>

      <Section title="6. Payments &amp; refunds" testId="tos-section-refunds">
        <p>
          Paid tiers renew automatically until you cancel. You can cancel
          anytime from your profile.
        </p>
        <p>
          <strong>14-day good-faith refund window.</strong> You may request a
          refund within 14 days of a fresh paid subscription, subject to
          these limits:
        </p>
        <ul>
          <li>New lessons unlock at a maximum of <strong>2 lessons per calendar day</strong> during the refund window. This prevents bulk downloading before refund.</li>
          <li>Once you have completed more than <strong>50% of any single course</strong>, your refund entitlement for that subscription is automatically voided. You keep full access.</li>
          <li>Requests must go through the <Link to="/help" className="underline">Help Center</Link>. Refunds outside the 14-day window are discretionary.</li>
        </ul>
      </Section>

      <Section title="7. Your content, our license" testId="tos-section-user-content">
        <p>
          You keep ownership of anything you post. By posting to public
          areas (forum, live sessions), you give us a worldwide, royalty-free
          license to display, distribute, and moderate that content within
          the Academy.
        </p>
      </Section>

      <Section title="8. Changes &amp; ending your account" testId="tos-section-changes">
        <p>
          We may update these terms &mdash; we&rsquo;ll flag material changes on
          the site and require you to re-accept them before continuing. You can
          close your account anytime; we&rsquo;ll delete personal data on request
          per our privacy policy, but public forum posts may remain visible
          (anonymized).
        </p>
      </Section>

      {/* ============================================================
           9. Canadian jurisdiction — makes the anti-piracy clause
              enforceable against overseas users.
         ============================================================ */}
      <Section title="9. Governing law &amp; exclusive Canadian jurisdiction" testId="tos-section-jurisdiction">
        <div className="not-prose flex items-start gap-2 p-3 my-3 rounded-xl border border-primary/40 bg-primary/5">
          <Scale className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs text-foreground/85 leading-relaxed m-0">
            <strong>By using Freeze Dry Academy from any country, you agree
            these Terms are governed by Canadian law and any dispute is
            resolved exclusively in the courts of Alberta, Canada.</strong>
          </p>
        </div>
        <p>
          These Terms of Use, and any non-contractual obligations arising out
          of or in connection with them, are governed by and construed in
          accordance with the laws of the Province of Alberta and the federal
          laws of Canada applicable therein, without regard to any
          conflict-of-laws principles.
        </p>
        <p>
          You <strong>irrevocably submit to the exclusive jurisdiction</strong> of
          the courts of the Province of Alberta, located in the City of
          Calgary or the City of Edmonton, for the resolution of any dispute,
          claim, action, or proceeding arising out of or in connection with
          these Terms or your use of Freeze Dry Academy. You waive any right
          to object to that venue on the grounds of{' '}
          <em>forum non conveniens</em>, inconvenient forum, or lack of
          personal jurisdiction.
        </p>
        <p>
          If you access the Academy from outside Canada, you do so on your
          own initiative and are responsible for compliance with local laws.
          The choice of Canadian law and Alberta venue applies regardless of
          your country of residence, citizenship, or the location from which
          you accessed the platform. Where mandatory local consumer-protection
          law of your country of residence prohibits this venue selection,
          the parties agree that the balance of these Terms remains in full
          force and effect.
        </p>
        <p>
          Any legal proceedings shall be conducted in the English language.
          The <em>United Nations Convention on Contracts for the International
          Sale of Goods</em> is expressly excluded.
        </p>
        <p>
          If any provision of these Terms is held unenforceable, the balance
          shall remain in effect. Our failure to enforce any right is not a
          waiver of that right.
        </p>
      </Section>

      <Section title="10. Contact" testId="tos-section-contact">
        <p>
          Questions? Head to the <Link to="/help" className="underline">Help Center</Link>{' '}
          or Direct Message an admin from the forum. Legal notices should be
          addressed to Peak and Prairie Industries Inc., Alberta, Canada.
        </p>
      </Section>
    </div>
  </div>
  );
};

export default TermsOfUse;
